// useMemo removed - was blocking live updates by caching denormalized procedures

interface Physician {
  id: number;
  name: string;
  color?: string;
}

interface Anesthesiologist {
  id: number;
  name: string;
}

interface Patient {
  id: number;
  status?: string;
  events?: any;
}

interface Procedure {
  id: number;
  physicianId?: number;
  physician?: Physician | null;
  anesthesiologistId?: number;
  anesthesiologist?: Anesthesiologist | null;
  patientId?: number;
  patient?: Patient | null;
  [key: string]: any;
}

interface Entities {
  physicians?: Physician[];
  anesthesiologists?: Anesthesiologist[];
  patients?: Patient[];
}

interface EntityLookups {
  physicians: Map<number, Physician>;
  anesthesiologists: Map<number, Anesthesiologist>;
  patients: Map<number, Patient>;
}

export function createEntityLookups(
  physicians: Physician[] = [],
  anesthesiologists: Anesthesiologist[] = [],
  patients: Patient[] = []
): EntityLookups {
  return {
    physicians: new Map(physicians.map(p => [p.id, p])),
    anesthesiologists: new Map(anesthesiologists.map(a => [a.id, a])),
    patients: new Map(patients.map(p => [p.id, p])),
  };
}

export function denormalizeProcedure(
  procedure: Procedure | null | undefined,
  lookups: EntityLookups
): Procedure | null {
  if (!procedure) return null;

  return {
    ...procedure,
    physician: procedure.physician || (procedure.physicianId 
      ? lookups.physicians.get(procedure.physicianId) || null
      : null),
    anesthesiologist: procedure.anesthesiologist || (procedure.anesthesiologistId
      ? lookups.anesthesiologists.get(procedure.anesthesiologistId) || null
      : null),
    patient: procedure.patient || (procedure.patientId
      ? lookups.patients.get(procedure.patientId) || null
      : null),
  };
}

export function denormalizeProcedures(
  procedures: Procedure[] = [],
  lookups: EntityLookups
): Procedure[] {
  return procedures.map(procedure => denormalizeProcedure(procedure, lookups)).filter(Boolean) as Procedure[];
}

export function useDenormalizedProcedures(
  procedures: Procedure[] | undefined,
  entities: Entities | undefined
): Procedure[] {
  // Don't use useMemo - it blocks live updates!
  // When writeFragment updates the cache, the procedures array reference doesn't change
  // useMemo sees same reference and returns cached result, preventing re-renders
  // Denormalization is fast enough to run on every render (< 1ms)
  const lookups = createEntityLookups(
    entities?.physicians,
    entities?.anesthesiologists,
    entities?.patients
  );
  return denormalizeProcedures(procedures, lookups);
}
